import type { OrganizationSocialLink } from '@re/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { OrgSettings } from '../api/types';
import { useAuth } from '../auth/AuthContext';

const BRAND_KEY = 'org.lastBrand';

export type OrgBrand = {
  slug: string;
  name: string;
  nameAr?: string | null;
  name_ar?: string | null;
  nameEn?: string | null;
  name_en?: string | null;
  logoUrl: string | null;
  primaryColor?: string | null;
};

export function readLastBrand(): OrgBrand | null {
  try {
    const raw = localStorage.getItem(BRAND_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as OrgBrand;
    return v && typeof v.slug === 'string' ? v : null;
  } catch {
    return null;
  }
}

export function writeLastBrand(brand: OrgBrand) {
  try {
    localStorage.setItem(BRAND_KEY, JSON.stringify(brand));
  } catch {
    /* ignore */
  }
}

export async function fetchOrgSettings(): Promise<OrgSettings> {
  const s = await api<OrgSettings>('/settings');
  // Login-page logo hint only — theme/branding always from API responses, not from this cache.
  writeLastBrand({
    slug: s.slug,
    name: s.name,
    nameAr: s.nameAr,
    name_ar: s.name_ar,
    nameEn: s.nameEn,
    name_en: s.name_en,
    logoUrl: s.logoUrl,
    primaryColor: s.primaryColor ?? s.theme?.primaryColor ?? null,
  });
  return s;
}

export function useOrgSocialLinks(): OrganizationSocialLink[] {
  const { status } = useAuth();
  const q = useQuery({ queryKey: ['settings'], queryFn: fetchOrgSettings, enabled: status === 'authenticated', staleTime: 60_000 });
  return q.data?.socialLinks ?? [];
}

/** Company logo URL after login; null when the org has not uploaded one. */
export function useOrgLogo(): string | null {
  const { status } = useAuth();
  const q = useQuery({ queryKey: ['settings'], queryFn: fetchOrgSettings, enabled: status === 'authenticated', staleTime: 60_000 });
  return q.data?.logoUrl ?? null;
}
